import type { Plugin } from '#app';
import type { ISocketOptions, SocketService } from '~/utils/services/socket.service';
import { initSocketService } from '~/utils/services/socket.service';

export interface SocketPlugin {
  init: (options: ISocketOptions, callback: () => void) => Promise<void>;
  on: (...args: any[]) => void;
  off: (...args: any[]) => void;
  emit: (...args: any[]) => void;
  disconnect: () => void;
  connect: () => void;
  isDebugEnabled: () => boolean;
}

declare module '#app' {
  interface NuxtApp {
    $socket: SocketPlugin;
  }
}

let socketService: SocketService | null = null;

/**
 * Lightweight socket plugin stub
 * Socket.io-client is lazy loaded only after user authentication
 * This reduces initial bundle size by ~35KB (gzipped)
 */
export default defineNuxtPlugin(() => {
  return {
    provide: {
      socket: {
        async init(options: ISocketOptions = {}, callback = () => {}) {
          try {
            if (!socketService) {
              socketService = await initSocketService();
            }
            socketService.init(options, callback);
          } catch (error) {
            console.error('Failed to initialize socket:', error);
          }
        },

        on(...args: any[]) {
          if (!socketService) {
            console.warn('Socket.io not loaded yet. Call init() first after login.');
            return;
          }
          socketService.on(args[0], args[1]);
        },

        off(...args: any[]) {
          if (!socketService) return;
          socketService.off(args[0], args[1]);
        },

        emit(...args: any[]) {
          if (!socketService) {
            console.warn('Socket.io not loaded yet. Call init() first after login.');
            return;
          }
          const [event, ...data] = args;
          socketService.emit(event, ...data);
        },

        disconnect() {
          if (!socketService) return;
          socketService.disconnect();
        },

        connect() {
          if (!socketService) {
            console.warn('Socket.io not loaded yet. Call init() first after login.');
            return;
          }
          socketService.connect();
        },

        isDebugEnabled() {
          return socketService?.isDebugEnabled() ?? false;
        },
      } as SocketPlugin,
    },
  };
}) as Plugin;
